import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  AlertTriangle,
  AlertCircle,
  Info,
  ShieldAlert,
  CheckCircle,
  Eye,
  Clock,
  ChevronDown,
  ChevronRight,
  User,
  Database,
  Calendar,
  Loader2,
  XCircle,
  FileText,
  Lightbulb,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { format, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import type { ComplianceAlert, AlertSeverity, AlertStatus } from '@/types/compliance';
import {
  alertSeverityLabels,
  alertStatusLabels,
  alertSeverityColors,
  alertStatusColors,
} from '@/types/compliance';

const severityIcons: Record<AlertSeverity, React.ElementType> = {
  info: Info,
  low: AlertCircle,
  medium: AlertTriangle,
  high: ShieldAlert,
  critical: ShieldAlert,
};

const severityBgColors: Record<AlertSeverity, string> = {
  info: 'bg-gray-50 border-gray-200',
  low: 'bg-green-50 border-green-200',
  medium: 'bg-yellow-50 border-yellow-200',
  high: 'bg-orange-50 border-orange-200',
  critical: 'bg-red-50 border-red-200',
};

const severityIconColors: Record<AlertSeverity, string> = {
  info: 'text-gray-500',
  low: 'text-green-500',
  medium: 'text-yellow-600',
  high: 'text-orange-500',
  critical: 'text-red-500',
};

interface AlertCardProps {
  alert: ComplianceAlert;
  onAcknowledge?: (alert: ComplianceAlert) => void;
  onResolve?: (alert: ComplianceAlert, notes: string) => void;
  onDismiss?: (alert: ComplianceAlert) => void;
  isAcknowledging?: boolean;
  isResolving?: boolean;
}

export function AlertCard({
  alert,
  onAcknowledge,
  onResolve,
  onDismiss,
  isAcknowledging,
  isResolving,
}: AlertCardProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [showResolveForm, setShowResolveForm] = useState(false);

  const Icon = severityIcons[alert.severity];
  const isResolved = alert.status === 'resolved' || alert.status === 'false_positive';
  const isPending = alert.status === 'open' || alert.status === 'acknowledged';

  const handleResolve = () => {
    if (resolutionNotes.trim()) {
      onResolve?.(alert, resolutionNotes);
      setShowResolveForm(false);
      setResolutionNotes('');
    }
  };

  return (
    <Card
      className={cn(
        "border-l-4 transition-all",
        alert.severity === 'critical' && "border-l-red-500",
        alert.severity === 'high' && "border-l-orange-500",
        alert.severity === 'medium' && "border-l-yellow-500",
        alert.severity === 'low' && "border-l-green-500",
        alert.severity === 'info' && "border-l-gray-400",
        severityBgColors[alert.severity],
        isResolved && "opacity-60"
      )}
    >
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div className="p-4">
          {/* Header */}
          <div className="flex items-start gap-3">
            {/* Icon */}
            <div
              className={cn(
                "flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center",
                alert.severity === 'critical' && "bg-red-100",
                alert.severity === 'high' && "bg-orange-100",
                alert.severity === 'medium' && "bg-yellow-100",
                alert.severity === 'low' && "bg-green-100",
                alert.severity === 'info' && "bg-gray-100"
              )}
            >
              <Icon className={cn("w-5 h-5", severityIconColors[alert.severity])} />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <Badge className={cn(alertSeverityColors[alert.severity])}>
                  {alertSeverityLabels[alert.severity]}
                </Badge>
                <Badge variant="outline" className={cn(alertStatusColors[alert.status])}>
                  {alertStatusLabels[alert.status]}
                </Badge>
                {alert.sla_breach && (
                  <Badge variant="destructive" className="text-xs">
                    SLA Incumplido
                  </Badge>
                )}
                {alert.risk_score > 0 && (
                  <Badge variant="outline" className="text-xs">
                    Riesgo: {alert.risk_score}%
                  </Badge>
                )}
              </div>

              <h4 className="font-semibold text-base">{alert.title}</h4>
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                {alert.description}
              </p>

              {/* Meta info */}
              <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {formatDistanceToNow(new Date(alert.detected_at), { addSuffix: true, locale: es })}
                </span>
                {alert.entity_type && (
                  <span className="flex items-center gap-1">
                    <Database className="h-3 w-3" />
                    {alert.entity_type}
                  </span>
                )}
                {alert.detection_source && (
                  <span className="flex items-center gap-1">
                    <FileText className="h-3 w-3" />
                    {alert.detection_source}
                  </span>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {isPending && (
                <>
                  {alert.status === 'open' && onAcknowledge && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onAcknowledge(alert)}
                      disabled={isAcknowledging}
                    >
                      {isAcknowledging ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Eye className="h-4 w-4 mr-1" />
                          Reconocer
                        </>
                      )}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={() => setShowResolveForm(!showResolveForm)}
                  >
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Resolver
                  </Button>
                </>
              )}
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  {isOpen ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </Button>
              </CollapsibleTrigger>
            </div>
          </div>

          {/* Resolve Form */}
          {showResolveForm && isPending && (
            <div className="mt-4 p-3 bg-background rounded-lg border space-y-3">
              <p className="text-sm font-medium">Resolver Alerta</p>
              <Textarea
                placeholder="Describa como se resolvio esta alerta..."
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                rows={3}
              />
              <div className="flex justify-end gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowResolveForm(false)}
                >
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    onDismiss?.(alert);
                    setShowResolveForm(false);
                  }}
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  Falso Positivo
                </Button>
                <Button
                  size="sm"
                  onClick={handleResolve}
                  disabled={!resolutionNotes.trim() || isResolving}
                >
                  {isResolving ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-1" />
                  )}
                  Confirmar
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Expanded Content */}
        <CollapsibleContent>
          <div className="px-4 pb-4 space-y-4 border-t pt-4">
            {/* Recommendation */}
            {alert.recommendation && (
              <div className="flex gap-3 p-3 bg-blue-50 rounded-lg">
                <Lightbulb className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-900">Recomendacion</p>
                  <p className="text-sm text-blue-700">{alert.recommendation}</p>
                </div>
              </div>
            )}

            {/* Details Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Detectado</p>
                <p className="font-medium">
                  {format(new Date(alert.detected_at), 'dd/MM/yyyy HH:mm', { locale: es })}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Tipo</p>
                <p className="font-medium">{alert.alert_type.replace(/_/g, ' ')}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Categoria</p>
                <p className="font-medium">{alert.category}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Regla</p>
                <p className="font-medium">{alert.detection_rule || 'N/A'}</p>
              </div>
            </div>

            {/* Affected Standards */}
            {alert.affected_standards && alert.affected_standards.length > 0 && (
              <div>
                <p className="text-sm text-muted-foreground mb-2">Estandares Afectados</p>
                <div className="flex gap-2">
                  {alert.affected_standards.map((std, i) => (
                    <Badge key={i} variant="outline">
                      {std}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Resolution info if resolved */}
            {isResolved && alert.resolution_notes && (
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="text-sm font-medium text-green-900">Resolucion</p>
                <p className="text-sm text-green-700">{alert.resolution_notes}</p>
                {alert.resolved_at && (
                  <p className="text-xs text-green-600 mt-1">
                    Resuelto el {format(new Date(alert.resolved_at), 'dd/MM/yyyy HH:mm', { locale: es })}
                  </p>
                )}
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
